import { useState, useCallback, useMemo } from 'react'
import { DocumentUploadCard } from '@/componentsV1/FileUpload/DocumentUploadCard'
import FileUploadModal, {
  UploadedFile,
} from '@/componentsV1/FileUpload/FileUploadModal'
import { UploadDocumentType } from '@/constants/businessConstants'
import { PrefixedUserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import type { IFixedAssetDepreciationStepContext } from '@/features/bookkeeping/end-of-year-review/types'
import type { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { migrateAssetStepContext } from '@/features/bookkeeping/end-of-year-review/admin/helpers'
import { useReselector } from '@/utils/sharedHooks'
import { selectRequiredReceiptCountForYear } from '@/features/transactions/transactions.selectors'
import { useReceiptReuploadStatus } from '../../../hooks'
import './styles.scss'

export interface AssetDepreciationDocumentProps {
  stepContext: IFixedAssetDepreciationStepContext | undefined
  setStepContext: (context: IFixedAssetDepreciationStepContext) => void
  userDocuments: UserDocument[]
  isFollowUp: boolean
  taxYear: number
  stepStatus?: string
}

const { assetsGreater2500Receipts } = PrefixedUserDocumentCategoryIdentifier

/**
 * AssetDepreciationDocument
 *
 * Reusable component for managing asset depreciation document uploads
 * for assets over $2,500. This is a provider-less document type with
 * receipt-based logic.
 *
 * Features:
 * - Receipt uploads for assets > $2,500 (Equipment, Computer: Hardware, Furniture)
 * - Optional "Other documents" for additional asset-related uploads
 * - Embedded FileUploadModal for uploads
 * - Uses existing V1 UI components (DocumentUploadCard, FileUploadModal)
 * - Custom data structure (receipts array, not standard documents array)
 *
 * Data Structure Note:
 * Unlike other document types that use a standard `documents` array,
 * asset depreciation uses a custom structure with `receipts` and
 * `otherDocumentIds` arrays. This is historical and may be normalized
 * in future refactoring to use the standard pattern.
 *
 * Provider-less Pattern:
 * - No provider parameter needed
 * - Does NOT include provider field in document objects
 * - Focused on asset receipt uploads regardless of payroll/contractor provider
 *
 * Receipt-based Logic:
 * - Tracks receipts for specific asset transactions > $2,500
 * - May have a required count based on transactions needing receipts
 * - Status shows success when required count is met
 *
 * @example
 * ```tsx
 * <AssetDepreciationDocument
 *   stepContext={assetsContext}
 *   setStepContext={setAssetsContext}
 *   userDocuments={allUserDocuments}
 *   isFollowUp={false}
 *   taxYear={2024}
 * />
 * ```
 */
export function AssetDepreciationDocument({
  stepContext,
  setStepContext,
  userDocuments,
  isFollowUp,
  taxYear,
}: AssetDepreciationDocumentProps) {
  // Select required receipt count directly from Redux
  const requiredReceiptCount = useReselector(
    selectRequiredReceiptCountForYear,
    taxYear.toString()
  )
  const [uploadDialog, setUploadDialog] = useState(false)
  const [fileType, setFileType] = useState<'receipt' | 'other'>('receipt')

  // Migrate legacy receiptIds to receipts array for backward compatibility
  const migratedContext = useMemo(
    () => migrateAssetStepContext(stepContext),
    [stepContext]
  )

  const receipts = useMemo(
    () => migratedContext.receipts ?? [],
    [migratedContext]
  )

  const receiptDocuments = useMemo(
    () =>
      userDocuments.filter((doc) =>
        receipts.some((receipt) => receipt.documentId === doc.id)
      ),
    [userDocuments, receipts]
  )

  const otherDocs = useMemo(
    () =>
      userDocuments.filter((doc) =>
        (migratedContext?.otherDocumentIds ?? []).includes(doc.id)
      ),
    [userDocuments, migratedContext?.otherDocumentIds]
  )

  // Calculate status based on required count (if provided) or just presence of receipts
  const receiptStatus = useMemo(() => {
    if (requiredReceiptCount !== undefined && requiredReceiptCount > 0) {
      return receipts.length >= requiredReceiptCount ? 'success' : 'none'
    }
    return receipts.length > 0 ? 'success' : 'none'
  }, [receipts.length, requiredReceiptCount])

  const otherDocsStatus = useMemo(
    () => (otherDocs.length > 0 ? 'success' : 'disabled'),
    [otherDocs.length]
  )

  // Get aggregated re-upload status for receipts (handles multiple receipts)
  const reuploadStatus = useReceiptReuploadStatus(migratedContext, isFollowUp)

  // Count receipts that don't need re-upload for the upload modal
  // This allows re-uploading when receipts need to be replaced
  const validReceiptCount = useMemo(() => {
    if (!isFollowUp) return receipts.length
    return receipts.filter((r) => !r.requestReUpload || r.updatedByUser).length
  }, [receipts, isFollowUp])

  const handleUploadClick = useCallback((type: 'receipt' | 'other') => {
    setFileType(type)
    setUploadDialog(true)
  }, [])

  const handleFileUploaded = useCallback(
    (uploadedFiles: UploadedFile[]) => {
      if (fileType === 'receipt') {
        // Find receipts that need re-upload (those without documentId or with requestReUpload)
        // Prioritize receipts without documentId (from deletion) first
        const receiptsWithoutDocumentId = receipts.filter(
          (r) => !r.documentId && r.requestReUpload === true
        )
        const receiptsWithDocumentIdNeedingReupload = receipts.filter(
          (r) => r.documentId && r.requestReUpload === true
        )
        const receiptsNeedingReupload = [
          ...receiptsWithoutDocumentId,
          ...receiptsWithDocumentIdNeedingReupload,
        ]

        // Map each uploaded file to replace one receipt needing re-upload (by index)
        // This allows incremental uploads - upload one at a time to replace receipts
        const newReceiptsFromUploads = uploadedFiles.map((file, index) => {
          const correspondingReceipt = receiptsNeedingReupload[index]

          // Check if there are still other receipts needing re-upload after this upload
          const remainingNeedingReupload = receiptsNeedingReupload.slice(
            index + 1
          )
          const stillNeedingReupload = remainingNeedingReupload.length > 0

          return {
            documentId: file.id,
            // Preserve requestReUpload and adminNote from the corresponding receipt
            // If there are still other receipts needing re-upload, also set requestReUpload
            // to keep the card red until all receipts are re-uploaded
            requestReUpload:
              correspondingReceipt?.requestReUpload ||
              (isFollowUp && stillNeedingReupload ? true : undefined),
            adminNote: correspondingReceipt?.adminNote,
            updatedByUser: true,
          }
        })

        // Remove only the receipts that are being replaced
        // Match receipts by their position: receipts with documentId by documentId,
        // receipts without documentId by their relative position among receipts without documentId
        const receiptsToKeep = receipts.filter((r, originalIndex) => {
          // Find the index of this receipt in receiptsNeedingReupload
          let indexInNeeding = -1

          if (r.documentId) {
            // For receipts with documentId, match by documentId
            indexInNeeding = receiptsNeedingReupload.findIndex(
              (needing) => needing.documentId === r.documentId
            )
          } else {
            // For receipts without documentId, match by relative position
            // Count how many receipts without documentId come before this one in original array
            const positionInOriginal = receipts
              .slice(0, originalIndex)
              .filter((orig) => !orig.documentId).length

            // Find the receipt in receiptsNeedingReupload at the same relative position
            let positionInNeeding = 0
            for (let i = 0; i < receiptsNeedingReupload.length; i++) {
              const needing = receiptsNeedingReupload[i]
              if (!needing.documentId) {
                if (positionInNeeding === positionInOriginal) {
                  // Check if properties match
                  if (
                    r.requestReUpload === needing.requestReUpload &&
                    r.adminNote === needing.adminNote
                  ) {
                    indexInNeeding = i
                    break
                  }
                }
                positionInNeeding++
              }
            }
          }

          // Keep receipts that aren't being replaced (index >= number of uploads or not found)
          return indexInNeeding === -1 || indexInNeeding >= uploadedFiles.length
        })

        // Combine kept receipts with new uploads
        const newReceipts = [...receiptsToKeep, ...newReceiptsFromUploads]
        setStepContext({
          ...migratedContext,
          receipts: newReceipts,
          // Always clear receiptIds (deprecated field)
          receiptIds: [],
        })
      } else {
        const otherDocumentIds = [
          ...(migratedContext.otherDocumentIds ?? []),
          ...uploadedFiles.map((file) => file.id),
        ]
        setStepContext({ ...migratedContext, otherDocumentIds })
      }

      setUploadDialog(false)
    },
    [fileType, migratedContext, receipts, setStepContext, isFollowUp]
  )

  const handleDocumentDeleted = useCallback(
    (documentId: number) => {
      // Find the receipt being deleted
      const deletedReceipt = receipts.find((r) => r.documentId === documentId)

      // If in follow-up mode and the receipt had requestReUpload, preserve that state
      // by keeping an entry without documentId so the card shows red state
      if (isFollowUp && deletedReceipt?.requestReUpload) {
        const preservedReceipt = {
          // No documentId - this indicates the receipt needs to be re-uploaded
          requestReUpload: true,
          adminNote: deletedReceipt.adminNote,
          // Don't preserve updatedByUser since the receipt was deleted
        }

        const newReceipts = [
          ...receipts.filter((r) => r.documentId !== documentId),
          preservedReceipt,
        ]

        setStepContext({
          ...migratedContext,
          receipts: newReceipts,
          // Always clear receiptIds (deprecated field)
          receiptIds: [],
          // Preserve otherDocumentIds to prevent step from being considered empty
          otherDocumentIds: migratedContext?.otherDocumentIds ?? [],
        })
      } else {
        // Normal deletion - just remove the receipt
        const newReceipts = receipts.filter((r) => r.documentId !== documentId)
        setStepContext({
          ...migratedContext,
          receipts: newReceipts,
          // Always clear receiptIds (deprecated field)
          receiptIds: [],
          // Preserve otherDocumentIds to prevent step from being considered empty
          otherDocumentIds: migratedContext?.otherDocumentIds ?? [],
        })
      }
    },
    [migratedContext, receipts, setStepContext, isFollowUp]
  )

  return (
    <>
      <div className="asset-depreciation-document">
        <h2>Assets over $2,500</h2>

        <div className="asset-depreciation-document__card-container">
          <DocumentUploadCard
            name="Receipts for Assets >$2,500"
            description={
              requiredReceiptCount && requiredReceiptCount > 0
                ? `Upload ${requiredReceiptCount} receipt${requiredReceiptCount > 1 ? 's' : ''} for assets over $2,500.`
                : 'Upload receipts for any assets over $2,500 purchased this year.'
            }
            status={receiptStatus}
            requiredDocCount={requiredReceiptCount}
            documents={receiptDocuments}
            handleUploadBtn={() => handleUploadClick('receipt')}
            onDocumentDeleted={handleDocumentDeleted}
            isFollowUp={isFollowUp}
            isRequiredReviewed={isFollowUp ? reuploadStatus : undefined}
          />

          <DocumentUploadCard
            name="Other documents"
            description="Upload any other documents you'd like to share with the bookkeeping team here."
            status={otherDocsStatus}
            documents={otherDocs}
            handleUploadBtn={() => handleUploadClick('other')}
            isFollowUp={isFollowUp}
          />
        </div>
      </div>

      <FileUploadModal
        open={uploadDialog}
        close={() => setUploadDialog(false)}
        // It's non standard to keep this as a receipt instead of bookkeeping but leaving to avoid downsteam effects
        // https://www.notion.so/heard/EOY-Bookkeeping-Document-Mapping-Triage-2e1591be525780ff96b9d39f777fa094
        documentType={
          fileType === 'receipt'
            ? UploadDocumentType.RECEIPT
            : UploadDocumentType.OTHER
        }
        categoryInternalName={
          fileType === 'receipt'
            ? assetsGreater2500Receipts(taxYear.toString())
            : undefined
        }
        userFacing
        year={taxYear.toString()}
        setUploadedFiles={handleFileUploaded}
        isUploadOnly
        requiredDocumentCount={
          fileType === 'receipt' ? requiredReceiptCount : undefined
        }
        uploadedDocumentCount={
          fileType === 'receipt' ? validReceiptCount : undefined
        }
      />
    </>
  )
}
